import type { AuditEvent } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import {
  ProposalMethod,
  type ProposalSource,
  QueuedEditsProposalAuditEvent,
} from '@seen/core/models/audit/QueuedEditsProposalAuditEvent'
import { cn, HoverCard, HoverCardContent, HoverCardTrigger } from '@seen/ui'
import {
  IconBolt,
  IconCheck,
  IconCopy,
  IconHistory,
  IconSparkles,
  IconUser,
} from '@tabler/icons-react'
import { format, formatDistanceToNow } from 'date-fns'
import { orderBy } from 'lodash-es'
import type { JSX } from 'react'
import { ExternalLink } from '#src/components/ExternalLink.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { useCopyToClipboard } from '#src/hooks/useCopyToClipboard.ts'
import { useCurrentPractitionerPerms } from '#src/hooks/useCurrentPractitionerPerms.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface QueuedEditsProposalSourceProps {
  // The proposal Bundle whose source events to look up, e.g. `Bundle/abc`.
  bundleReference: string
  // Also render the raw correlation ids as mono key/value rows.
  showTechnical?: boolean
  /* Escape hatch for stories and tests: render these events instead of
   * searching. Nothing in the app passes it — the component is search-driven in
   * product. */
  events?: AuditEvent[]
  className?: string
}

const METHOD_ICONS: Record<ProposalMethod, typeof IconBolt> = {
  [ProposalMethod.Automated]: IconBolt,
  [ProposalMethod.Assisted]: IconSparkles,
  [ProposalMethod.Direct]: IconUser,
}

// Last path segment of a reference, so a thread chip stays short.
function shortId(reference: string): string {
  const id = reference.split('/').at(-1) ?? reference
  return id.slice(-6)
}

// One compact line saying where a queued-edits proposal came from — the
// method that produced it, the human behind it, and the correlation ids a
// reviewer needs to trace it back. Reads every QueuedEditsProposalAuditEvent
// written against the proposal: a proposal accumulates one per modification, so
// the inline line shows the most recent and a hovercard carries the full
// history. Renders nothing while loading or when no event exists, so proposals
// made before this was recorded stay silent rather than showing an empty row.
export function QueuedEditsProposalSource({
  bundleReference,
  showTechnical = false,
  events,
  className,
}: QueuedEditsProposalSourceProps): JSX.Element | null {
  const { isTech } = useCurrentPractitionerPerms()
  const { copy, copied } = useCopyToClipboard()

  /* Silent: a proposal that predates source recording, or an AuditEvent the
   * reader may not search, must drop this row — not toast an error. */
  const { data } = useSearch(
    QueuedEditsProposalAuditEvent,
    events
      ? undefined
      : QueuedEditsProposalAuditEvent.byProposal({
          reference: bundleReference,
        }),
    { silentErrors: true }
  )

  // `byProposal` sorts newest-first; the history reads oldest→newest, so the
  // tail is the current source.
  const instances = orderBy(
    events
      ? events.map((event) =>
          QueuedEditsProposalAuditEvent.fromFhirResource(event)
        )
      : (data ?? []),
    [(instance) => instance.recorded],
    ['asc']
  )

  const latest = instances.at(-1)
  const source = latest?.source

  if (!source) {
    return null
  }

  const traceId = source.trace?.id

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-1 text-muted-foreground text-xs',
        className
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
        <SourceLine source={source} recorded={latest?.recorded} />

        {/* Trace ids are internal debugging handles. The tracing system can be
            any vendor, so this shared package exposes the opaque id rather than
            trying to construct a vendor URL. */}
        {isTech && traceId && (
          <button
            type="button"
            title="Source trace id"
            onClick={() => void copy(traceId)}
            className="ml-1 inline-flex items-center gap-1 font-mono text-2xs hover:text-foreground"
          >
            {copied ? (
              <IconCheck className="size-3" />
            ) : (
              <IconCopy className="size-3" />
            )}
            {shortId(traceId)}
          </button>
        )}

        {source.thread?.reference && (
          <span className="text-muted-foreground/60">
            · thread …{shortId(source.thread.reference)}
          </span>
        )}

        {instances.length > 1 && (
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <button
                type="button"
                className="ml-1 inline-flex shrink-0 items-center gap-1 hover:text-foreground"
              >
                <IconHistory className="size-3" />
                {instances.length} events
              </button>
            </HoverCardTrigger>
            <HoverCardContent className="w-96" align="start">
              <div className="space-y-3">
                <h4 className="text-sm font-semibold">Proposal history</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {instances.map((instance, index) => (
                    <HistoryRow
                      // Not `instance.id`: that throws on the unpersisted
                      // resources the `events` override builds.
                      key={`${instance.recorded}-${index}`}
                      instance={instance}
                    />
                  ))}
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        )}
      </div>

      {showTechnical &&
        source.metadata &&
        Object.keys(source.metadata).length > 0 && (
          <dl className="flex flex-col gap-0.5 rounded bg-muted/50 px-2 py-1.5 font-mono text-2xs">
            {Object.entries(source.metadata).map(([key, value]) => (
              <div key={key} className="flex gap-2">
                <dt className="text-muted-foreground/70">{key}:</dt>
                <dd className="min-w-0 break-all">{JSON.stringify(value)}</dd>
              </div>
            ))}
          </dl>
        )}
    </div>
  )
}

// One event's icon + method phrase + time. Shared by the inline latest row
// (relative time, alongside the copy/trace and thread chips the caller adds)
// and the hovercard history rows (absolute time).
function SourceLine({
  source,
  recorded,
  absoluteTime = false,
}: {
  source: ProposalSource
  recorded?: string
  absoluteTime?: boolean
}): JSX.Element {
  const { method, observer, requestor, requestorName, sourceUrl } = source
  const Icon = METHOD_ICONS[method] ?? IconUser
  const nameplate = requestor ? (
    <PractitionerNameplate practitioner={requestor} size={3} />
  ) : requestorName ? (
    <span className="text-foreground-secondary">{requestorName}</span>
  ) : null

  return (
    <>
      <Icon className="size-3.5 shrink-0" />
      <Phrase
        method={method}
        applicationName={referenceDisplay(observer)}
        nameplate={nameplate}
      />

      {recorded && (
        <span className="text-muted-foreground/60">
          ·{' '}
          {absoluteTime
            ? format(new Date(recorded), 'MMM d, yyyy h:mm a')
            : formatDistanceToNow(new Date(recorded), { addSuffix: true })}
        </span>
      )}

      {sourceUrl && (
        <ExternalLink href={sourceUrl} label="Source" className="ml-1" />
      )}
    </>
  )
}

// One row of the history hovercard: the same phrase as the inline line, an
// absolute timestamp, and the author's free-text note when they left one.
function HistoryRow({
  instance,
}: {
  instance: QueuedEditsProposalAuditEvent
}): JSX.Element | null {
  const { source } = instance
  if (!source) {
    return null
  }

  return (
    <div className="flex flex-col gap-0.5 border-b pb-2 text-xs text-muted-foreground last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
        <SourceLine source={source} recorded={instance.recorded} absoluteTime />
      </div>
      {source.description && (
        <p className="text-2xs text-muted-foreground/70">
          {source.description}
        </p>
      )}
    </div>
  )
}

// The method-specific sentence. Product/vendor identity is read from the
// recorded application actor's display; without one, the wording stays neutral.
function Phrase({
  method,
  applicationName,
  nameplate,
}: {
  method: ProposalMethod
  applicationName?: string
  nameplate: JSX.Element | null
}): JSX.Element {
  switch (method) {
    case ProposalMethod.Automated:
      return (
        <>
          <span>
            Proposed automatically
            {applicationName ? ` by ${applicationName}` : ''}
          </span>
          {nameplate && (
            <>
              <span>· requested by</span>
              {nameplate}
            </>
          )}
        </>
      )
    case ProposalMethod.Assisted:
      return (
        <>
          <span>
            Proposed with{applicationName ? ` ${applicationName}` : ''}{' '}
            assistance{nameplate ? ' by' : ''}
          </span>
          {nameplate}
        </>
      )
    case ProposalMethod.Direct:
      return (
        <>
          <span>Proposed{nameplate ? ' by' : ' directly'}</span>
          {nameplate}
        </>
      )
  }
}
